import { useState } from "react";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceTypes = [
  "Raccoon Removal",
  "Squirrel and Chipmunk Removal",
  "Rat and Mouse Control",
  "Skunk Removal",
  "Opossum Control",
  "Beaver Management",
  "Bird Control",
  "Bat Removal",
  "Attic Cleanup",
  "Other or Not Sure"
];

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We will get back to you soon.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        service_type: "",
        message: ""
      });
    } catch (error) {
      toast.error("Failed to send message. Please try calling us instead.");
      console.error("Contact form error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="bg-primary py-16 lg:py-24" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="font-heading font-bold text-4xl md:text-5xl uppercase tracking-tight text-primary-foreground mb-6">
              Contact Us
            </h1>
            <p className="text-primary-foreground/80 font-body text-lg">
              Have a wildlife problem? Get in touch with us today for a free consultation and quote. 
              We respond to all inquiries promptly.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 px-4" data-testid="contact-content">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="font-heading font-bold text-2xl md:text-3xl uppercase tracking-tight text-foreground mb-8">
                Get In Touch
              </h2>
              
              <div className="space-y-6 mb-10">
                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 p-3 rounded-sm">
                    <Phone className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold uppercase text-foreground mb-1">Phone</h3>
                    <a
                      href="tel:343-263-2000"
                      className="text-accent hover:underline font-body text-lg"
                      data-testid="contact-phone"
                    >
                      343-263-2000
                    </a>
                    <p className="text-muted-foreground text-sm font-body">Call us for immediate assistance</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 p-3 rounded-sm">
                    <Mail className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold uppercase text-foreground mb-1">Email</h3>
                    <a
                      href="mailto:patrickenterprisescanada@gmail.com"
                      className="text-accent hover:underline font-body break-all"
                      data-testid="contact-email"
                    >
                      patrickenterprisescanada@gmail.com
                    </a>
                    <p className="text-muted-foreground text-sm font-body">We respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 p-3 rounded-sm">
                    <MapPin className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold uppercase text-foreground mb-1">Location</h3>
                    <p className="text-foreground font-body">529 Thrasher Rd</p>
                    <p className="text-foreground font-body">Plainfield, ON K0K 2V0</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 p-3 rounded-sm">
                    <Clock className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold uppercase text-foreground mb-1">Hours</h3>
                    <p className="text-foreground font-body">Monday - Friday: 9:00 AM - 5:00 PM</p>
                    <p className="text-muted-foreground text-sm font-body">Emergency services available</p>
                  </div>
                </div>
              </div>

              <div className="bg-accent/10 border-2 border-accent p-6 rounded-sm" data-testid="emergency-box">
                <h3 className="font-heading font-bold text-lg uppercase text-foreground mb-2">
                  Wildlife Emergency?
                </h3>
                <p className="text-muted-foreground font-body mb-4">
                  If you have an urgent wildlife situation, call us immediately.
                </p>
                <a
                  href="tel:343-263-2000"
                  className="inline-flex items-center gap-2 bg-accent text-accent-foreground px-6 py-3 rounded-sm font-heading uppercase tracking-wide rugged-shadow transition-all hover:bg-accent/90"
                  data-testid="emergency-call-btn"
                >
                  <Phone className="h-4 w-4" />
                  Call Now: 343-263-2000
                </a>
              </div>
            </div>

            <div className="bg-card border-2 border-secondary/20 p-8 rounded-sm" data-testid="contact-form-container">
              <h2 className="font-heading font-bold text-2xl uppercase tracking-tight text-foreground mb-6">
                Request a Free Quote
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <Label htmlFor="name" className="font-heading uppercase text-sm tracking-wide">Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="mt-2 border-2 border-secondary/30 focus:border-primary rounded-sm"
                    placeholder="Your name"
                    data-testid="input-name"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="font-heading uppercase text-sm tracking-wide">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="mt-2 border-2 border-secondary/30 focus:border-primary rounded-sm"
                    placeholder="your@email.com"
                    data-testid="input-email"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="font-heading uppercase text-sm tracking-wide">Phone</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="mt-2 border-2 border-secondary/30 focus:border-primary rounded-sm"
                    placeholder="(555) 555-5555"
                    data-testid="input-phone"
                  />
                </div>

                <div>
                  <Label htmlFor="service_type" className="font-heading uppercase text-sm tracking-wide">Service Needed</Label>
                  <Select value={formData.service_type} onValueChange={handleServiceChange}>
                    <SelectTrigger className="mt-2 border-2 border-secondary/30 focus:border-primary rounded-sm" data-testid="select-service">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceTypes.map((type) => (
                        <SelectItem key={type} value={type} data-testid={`service-option-${type.toLowerCase().replace(/\s+/g, "-")}`}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="message" className="font-heading uppercase text-sm tracking-wide">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="mt-2 border-2 border-secondary/30 focus:border-primary rounded-sm resize-none"
                    placeholder="Tell us about your wildlife problem..."
                    data-testid="input-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-heading uppercase tracking-wide rugged-shadow"
                  data-testid="submit-btn"
                >
                  {isSubmitting ? (
                    "Sending..."
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
